// Pseudo Manager page: Manager → Pseudo Managers → Reportees → Connect / Recruitment / Employee Central.
// Backed entirely by the real PseudoManager API/DB — see utils/pseudoManagerApi.ts.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, UserCog, UserPlus } from 'lucide-react';
import { EmployeeInfoBanner } from '../components/EmployeeInfoBanner';
import { PseudoManagerCard } from '../components/PseudoManagerCard';
import { AssignPseudoManagerModal } from '../components/AssignPseudoManagerModal';
import { Toast, ConfirmDialog } from '../components/Toast';
import { useUserProfileContext } from '../context/UserProfileContext';
import { useApiClient } from '../hooks/useApiClient';
import { useToast } from '../hooks/useToast';
import {
  assignPseudoManager,
  getPseudoManagers,
  getSelectablePoolForEdit,
  getUnassignedReportees,
  removePseudoManager,
  updateAssignedReportees,
} from '../utils/pseudoManagerApi';
import { PseudoManager, PseudoManagerCandidate, PseudoManagerReportee } from '../types/pseudoManager';

export function PseudoManagers() {
  const { userProfile, loading: profileLoading } = useUserProfileContext();
  const { apiCall } = useApiClient();
  const { toasts, hideToast, success, error } = useToast();
  const navigate = useNavigate();

  const [pseudoManagers, setPseudoManagers] = useState<PseudoManager[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingPseudoManagerId, setEditingPseudoManagerId] = useState<number | null>(null);
  const [pendingRemovalId, setPendingRemovalId] = useState<number | null>(null);

  // The Manager's real direct reports, mapped to the shape the Pseudo Manager APIs expect.
  const reporteePool: PseudoManagerReportee[] = useMemo(
    () =>
      (userProfile?.directReports ?? []).map((r) => ({
        employeeId: r.employeeId,
        displayName: r.displayName,
        email: r.email,
        jobTitle: r.jobTitle,
        department: r.department,
      })),
    [userProfile]
  );

  const loadPseudoManagers = useCallback(async () => {
    if (!userProfile?.employeeId) return;
    setLoadingAssignments(true);
    try {
      const list = await getPseudoManagers(userProfile.employeeId, apiCall);
      setPseudoManagers(list);
    } catch (err: any) {
      error(err?.message ?? 'Failed to load Pseudo Managers');
    } finally {
      setLoadingAssignments(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.employeeId, apiCall]);

  useEffect(() => {
    loadPseudoManagers();
  }, [loadPseudoManagers]);

  // A Pseudo Manager cannot elect their own Pseudo Manager — keep them off this page even via direct URL.
  useEffect(() => {
    if (!profileLoading && userProfile?.isPseudoManager) {
      navigate('/dashboard');
    }
  }, [profileLoading, userProfile?.isPseudoManager, navigate]);

  const unassignedReportees = useMemo(
    () => getUnassignedReportees(reporteePool, pseudoManagers),
    [reporteePool, pseudoManagers]
  );

  const editingPseudoManager = useMemo(
    () => pseudoManagers.find((pm) => pm.id === editingPseudoManagerId),
    [pseudoManagers, editingPseudoManagerId]
  );

  const selectablePool = useMemo(
    () => getSelectablePoolForEdit(reporteePool, pseudoManagers, editingPseudoManagerId ?? undefined),
    [reporteePool, pseudoManagers, editingPseudoManagerId]
  );

  const pendingRemoval = pseudoManagers.find((pm) => pm.id === pendingRemovalId);

  const closeModal = () => {
    setModalMode(null);
    setEditingPseudoManagerId(null);
  };

  const handleSubmit = async (candidate: PseudoManagerCandidate, reporteeIds: string[]) => {
    if (!userProfile) return;
    const reportees = selectablePool.filter((r) => reporteeIds.includes(r.employeeId));

    try {
      if (modalMode === 'edit' && editingPseudoManagerId != null) {
        await updateAssignedReportees(editingPseudoManagerId, reportees, userProfile.employeeId, apiCall);
        success(`Updated reportees for ${candidate.displayName}`);
      } else {
        await assignPseudoManager(
          {
            managerId: userProfile.employeeId,
            managerDisplayName: userProfile.displayName,
            managerEmail: userProfile.email,
            candidate,
            reportees,
          },
          apiCall
        );
        success(`${candidate.displayName} assigned as a Pseudo Manager`);
      }
      closeModal();
      await loadPseudoManagers();
    } catch (err: any) {
      error(err?.message ?? 'Failed to save Pseudo Manager assignment');
    }
  };

  const handleConfirmRemoval = async () => {
    if (!pendingRemovalId || !userProfile) return;
    const removed = pendingRemoval;
    try {
      await removePseudoManager(pendingRemovalId, userProfile.employeeId, apiCall);
      setPendingRemovalId(null);
      if (removed) success(`${removed.displayName} removed successfully`);
      await loadPseudoManagers();
    } catch (err: any) {
      error(err?.message ?? 'Failed to remove Pseudo Manager');
    }
  };

  if ((profileLoading && !userProfile) || (loadingAssignments && pseudoManagers.length === 0 && userProfile)) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-slate-600 animate-spin" />
          <p className="text-slate-600 font-medium">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        <EmployeeInfoBanner userProfile={userProfile} loading={profileLoading} />

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-slate-800">Pseudo Managers</h2>
          <button
            onClick={() => setModalMode('create')}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-sm hover:shadow-md transition-all duration-200"
          >
            <UserPlus className="w-4 h-4" />
            Assign Pseudo Manager
          </button>
        </div>

        {pseudoManagers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center mb-6">
            <UserCog className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No Pseudo Managers assigned yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Assign a Pseudo Manager to help review Connect, Recruitment and Employee Central for your reportees.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            {pseudoManagers.map((pm) => (
              <PseudoManagerCard
                key={pm.id}
                pseudoManager={pm}
                onManageReportees={() => {
                  setEditingPseudoManagerId(pm.id);
                  setModalMode('edit');
                }}
                onRemove={() => setPendingRemovalId(pm.id)}
              />
            ))}
          </div>
        )}

        {/* Unassigned reportees */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            Unassigned Reportees ({unassignedReportees.length})
          </h3>
          {unassignedReportees.length === 0 ? (
            <p className="text-sm text-slate-400 italic">Every reportee is covered by a Pseudo Manager.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {unassignedReportees.map((r) => (
                <span
                  key={r.employeeId}
                  title="Currently managed directly by you"
                  className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600"
                >
                  {r.displayName}
                </span>
              ))}
            </div>
          )}
        </div>
      </main>

      {modalMode && (
        <AssignPseudoManagerModal
          candidatePool={reporteePool}
          assignedPseudoManagerIds={pseudoManagers.map((pm) => pm.employeeId)}
          selectablePool={selectablePool}
          existingPseudoManager={modalMode === 'edit' ? editingPseudoManager : undefined}
          onClose={closeModal}
          onSubmit={handleSubmit}
        />
      )}

      {pendingRemoval && (
        <ConfirmDialog
          title="Remove Pseudo Manager"
          message={`${pendingRemoval.displayName} will no longer manage their assigned reportees. Those reportees will fall back to being managed directly by you.`}
          confirmText="Remove"
          type="danger"
          onConfirm={handleConfirmRemoval}
          onCancel={() => setPendingRemovalId(null)}
        />
      )}

      {toasts.map((t) => (
        <Toast key={t.id} message={t.message} type={t.type} onClose={() => hideToast(t.id)} />
      ))}
    </div>
  );
}
